import { useEffect } from 'react';
import { Link } from 'react-router-dom';

interface Term {
  name: string;
  def: React.ReactNode;
}

interface Group {
  title: string;
  intro?: string;
  terms: Term[];
}

const GROUPS: Group[] = [
  {
    title: 'Renamed in the product',
    intro:
      'The screens use the new name. Some stored and API names still carry the old one, and they are kept so existing integrations do not break.',
    terms: [
      {
        name: 'Meter',
        def: (
          <>
            The unit you measure, such as seats, GB or API calls. It was called a variable
            component. The old name remains in API and field names such as{' '}
            <span className="api-inline">variableType</span>,{' '}
            <span className="api-inline">variableSlug</span> and{' '}
            <span className="api-inline">variableId</span>, and old dashboard addresses under
            /catalog/variables redirect to Settings, Meters. See{' '}
            <Link to="/docs/concepts/meter">Meter</Link>. The priced rule attached to a meter is
            still called a <Link to="/docs/concepts/variableComponent">Variable Component</Link>.
          </>
        ),
      },
      {
        name: 'Regions',
        def: (
          <>
            A place you sell, with its own currency. It was called Markets. The dashboard page
            still lives at /market-config, and Markets survives in internal names such as the
            add-market screens. See <Link to="/docs/concepts/region">Region</Link>.
          </>
        ),
      },
      {
        name: 'Companies',
        def: (
          <>
            The CRM list of companies you are talking to, formerly Prospects. The old /prospects
            address redirects to /companies. The API still says prospect: the public GraphQL
            mutation is <span className="api-inline">createProspect</span>. See{' '}
            <Link to="/docs/guides/crm">the CRM guide</Link>.
          </>
        ),
      },
    ],
  },
  {
    title: 'People and organisations',
    terms: [
      {
        name: 'Client',
        def: (
          <>
            A company that is billed. It belongs to one Region and can hold subscriptions. See{' '}
            <Link to="/docs/concepts/client">Client</Link>.
          </>
        ),
      },
      {
        name: 'Company',
        def: 'A company record in the CRM. It becomes a client when it is billed; until then it is only a company you track.',
      },
      {
        name: 'Lead',
        def: (
          <>
            A deal in flight on a pipeline, with a stage, a value and an owner. A lead is a deal,
            not a person. See <Link to="/docs/concepts/lead">Lead</Link>.
          </>
        ),
      },
      {
        name: 'Contact',
        def: (
          <>
            A person at a client or company, the human you write to. See{' '}
            <Link to="/docs/concepts/contact">Contact</Link>.
          </>
        ),
      },
    ],
  },
  {
    title: 'Catalog and pricing',
    terms: [
      {
        name: 'Module',
        def: (
          <>
            A versioned set of capabilities and meters. Modules are the building blocks. See{' '}
            <Link to="/docs/concepts/module">Module</Link>.
          </>
        ),
      },
      {
        name: 'Bundle',
        def: (
          <>
            A versioned package of modules sold at one tier, such as Starter or Enterprise. See{' '}
            <Link to="/docs/concepts/bundle">Bundle</Link>.
          </>
        ),
      },
      {
        name: 'Plan',
        def: 'The everyday word for what a customer buys. In the catalog that is a bundle (or a single module sold on its own); there is no separate plan object.',
      },
      {
        name: 'Catalog version',
        def: (
          <>
            Every module, bundle and priced meter is a Draft you can edit, then Published, which
            freezes it, then Archived. A subscription pins the version it was sold on and stays
            there until you migrate it. A subscription left on an older version on purpose is
            grandfathered. See <Link to="/docs/guides/versioning">Versioning</Link>.
          </>
        ),
      },
      {
        name: 'Legacy plan',
        def: (
          <>
            A plan you no longer sell that existing customers stay on. See{' '}
            <Link to="/docs/guides/legacy-pricing">Legacy pricing</Link>.
          </>
        ),
      },
      {
        name: 'Pricing rule',
        def: (
          <>
            The earlier flat per-region price for a whole bundle or module. See{' '}
            <Link to="/docs/concepts/pricingRule">Pricing rule</Link> and{' '}
            <Link to="/docs/guides/scoped-pricing">Scoped pricing</Link>.
          </>
        ),
      },
      {
        name: 'Custom pricing agreement',
        def: (
          <>
            A negotiated override for one customer. It wins over the catalog price. See{' '}
            <Link to="/docs/concepts/customPricingAgreement">Custom pricing agreement</Link>.
          </>
        ),
      },
      {
        name: 'Contract',
        def: (
          <>
            The signed agreement a subscription is sold under and the committed term it carries.
            See <Link to="/docs/guides/contracts">Contracts</Link>.
          </>
        ),
      },
      {
        name: 'Discount',
        def: (
          <>
            A bounded, reversible price reduction. Its target is the base price, one component
            (meter), or the whole invoice. A base-price discount is worked out once over all the
            base lines together. See <Link to="/docs/guides/discounting">Discounting</Link>.
          </>
        ),
      },
    ],
  },
  {
    title: 'Billing and usage',
    terms: [
      {
        name: 'Billing schedule',
        def: (
          <>
            Separates when a subscription is invoiced from what it costs, for example a prepaid
            term or a charge held until go-live. See{' '}
            <Link to="/docs/guides/billing-schedules">Billing schedules</Link>.
          </>
        ),
      },
      {
        name: 'Period and seal',
        def: (
          <>
            A usage period closes at the end of the billing period. Sealing locks its final
            billable quantity, after which it can no longer change. See{' '}
            <Link to="/docs/guides/metered-usage">Metered usage</Link>.
          </>
        ),
      },
      {
        name: 'Usage reading',
        def: (
          <>
            One meter, one subscription, one closed period. It is Accumulating while late events
            can still arrive, Awaiting when you must enter a value, Unverified when nothing arrived
            (never billed as zero), Sealed once locked, and Waived when you release the invoice
            and bill nothing for it.
          </>
        ),
      },
      {
        name: 'Credit note',
        def: (
          <>
            A document that gives money back against an invoice, used instead of voiding an invoice
            that collected payment. See{' '}
            <Link to="/docs/guides/credit-notes">Credit notes</Link>.
          </>
        ),
      },
      {
        name: 'Lifecycle event',
        def: (
          <>
            An append-only record of each moment that moves revenue, with its change in MRR. See{' '}
            <Link to="/docs/concepts/lifecycleEvent">Lifecycle event</Link>.
          </>
        ),
      },
      {
        name: 'occurredAt and attributedAt',
        def: (
          <>
            Two dates on a lifecycle event. occurredAt is when Revvly learned of it and is what
            timelines show. attributedAt is the period the movement is counted in; for metered
            usage it is the start of the service period, so usage lands in the month it was used.
            See <Link to="/docs/guides/lifecycle-log">Lifecycle log</Link>.
          </>
        ),
      },
    ],
  },
  {
    title: 'Workspace and CRM',
    terms: [
      {
        name: 'Work queue',
        def: 'A CRM page of the tasks waiting on you and your accounts, so you can act on them in order.',
      },
      {
        name: 'Work Inbox',
        def: 'Your own sorted view of your connected Gmail under Inbox, split into Primary, Needs reply, Other, Snoozed and Done. Private to you, admins included.',
      },
      {
        name: 'Situations',
        def: 'Signals about a client, drawn from email and activity, such as a conversation waiting on you or a contact going quiet. They sit under Clients and also show on the client.',
      },
      {
        name: 'Library',
        def: (
          <>
            The sidebar group for Documents, Forms and Templates. See{' '}
            <Link to="/docs/guides/document-library">Document library</Link>.
          </>
        ),
      },
      {
        name: 'Conversation',
        def: 'In the CRM, the email thread with a client. Not the same as a Support ticket.',
      },
    ],
  },
  {
    title: 'OKRs',
    terms: [
      {
        name: 'Cycle',
        def: (
          <>
            One stretch of goals, usually a quarter. A cycle is Draft, Active, Scoring or Closed.
            See <Link to="/docs/guides/okrs">OKRs</Link>.
          </>
        ),
      },
      {
        name: 'Objective',
        def: 'Where you want to go, in words, at company or team level. An objective is Committed, a promise that missing it is a failure, or Aspirational, a stretch where falling short is acceptable. Aspirational is the default.',
      },
      {
        name: 'Key Result',
        def: 'How you will know: an owner, a unit (count, percentage or currency), a start, a target and a direction. The value is typed by hand or read from a Revvly metric.',
      },
      {
        name: 'Check-in',
        def: 'A recurring prompt to post a value and a confidence from 1 to 10 on a Key Result. Delivered by Slack or ClickUp Chat. A separate daily check-in in Slack, not tied to a Key Result, is described in Run daily check-ins in Slack.',
      },
      {
        name: 'Daily check-in',
        def: 'A set of questions Revvly asks chosen people in a Slack DM on working days, posting their answers and an end-of-day summary to a channel. Set up under Settings, Check-ins.',
      },
    ],
  },
  {
    title: 'Support',
    terms: [
      {
        name: 'Support ticket',
        def: (
          <>
            One request from a customer in Revvly Support, with a status, public replies and
            internal notes. See <Link to="/docs/guides/support-inbox">Support inbox</Link>.
          </>
        ),
      },
      {
        name: 'Support conversation',
        def: (
          <>
            The back-and-forth inside one ticket, as the end user sees it in your own chat. The
            ticket is the record; the conversation is how it is shown. See{' '}
            <Link to="/docs/api/support-chat">Support chat API</Link>.
          </>
        ),
      },
    ],
  },
];

export function TermsAndNames() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Terms and names — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/reference">Reference</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Terms and names</span>
      </nav>
      <header className="dp-header">
        <span className="rv-eyebrow-mini">Reference</span>
        <h1>
          Terms and <em>names</em>
        </h1>
        <p className="dp-oneliner">
          The words Revvly uses, what each one means, and where an older name still shows up in the
          API.
        </p>
      </header>

      {GROUPS.map((g, i) => (
        <section className="dp-section" key={g.title}>
          <h2>
            <span className="dp-step-num">{String(i + 1).padStart(2, '0')}</span>
            {g.title}
          </h2>
          {g.intro && <p className="dp-plain">{g.intro}</p>}
          <div className="dp-fields-table">
            {g.terms.map((t) => (
              <div className="dp-field-row" key={t.name}>
                <div className="dp-field-k">{t.name}</div>
                <div className="dp-field-v">{t.def}</div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
